'use client';

import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { usePathname } from 'next/navigation';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { SwitchButton } from '@/components/profile/buttons/SwitchButton';
import { useUserCanIssueBonuses } from '@/hooks/bonuses';
import { useNavigation } from '@/hooks/navigation';
import { sendBonusReceiveSettingsEvent } from '@/modules/googleAnalytics';
import { useDepositStore, useGetCommonStore } from '@/store';
import { ScReceiveBonusesNotice } from './styled';

interface IReceiveBonusesNotice {
  translation: string;
  warningTitle?: string;
  successTitle?: string;
  warningMessage: string;
  successMessage: string;
  onVisibilityChange?: (isVisible: boolean) => void;
}

export const ReceiveBonusesNotice: FC<IReceiveBonusesNotice> = ({
  warningTitle,
  successTitle,
  warningMessage,
  successMessage,
  translation,
  onVisibilityChange,
}) => {
  const { t } = useTranslation(['personal', 'deposits', translation]);
  const { navigate } = useNavigation();
  const useCommonStore = useGetCommonStore();
  const pathname = usePathname();

  const isUserCanIssueBonuses = useUserCanIssueBonuses();

  const { user, pendingUser, isAuthenticated, updateUser, refreshUser } =
    useCommonStore(
      useShallow((state) => ({
        user: state.user,
        pendingUser: state.pendingUser,
        isAuthenticated: state.isAuthenticated,
        updateUser: state.updateUser,
        refreshUser: state.refreshUser,
      }))
    );

  const handleChangeReceiveBonuses =
    useDepositStore.use.handleChangeReceiveBonuses();

  const [justEnabled, setJustEnabled] = useState(false);

  const shouldBeVisible = useMemo(
    () => !isUserCanIssueBonuses && isAuthenticated,
    [isUserCanIssueBonuses, isAuthenticated]
  );

  useEffect(() => {
    if (!justEnabled) return;

    const timer = setTimeout(() => setJustEnabled(false), 4000);
    return () => clearTimeout(timer);
  }, [shouldBeVisible, justEnabled]);

  const handleToggle = useCallback(() => {
    const newStatus = !isUserCanIssueBonuses;
    handleChangeReceiveBonuses(user, updateUser, refreshUser);

    if (!isUserCanIssueBonuses) setJustEnabled(true);

    sendBonusReceiveSettingsEvent({
      status: newStatus ? 'on' : 'off',
      source: pathname,
    });
  }, [
    isUserCanIssueBonuses,
    user,
    updateUser,
    refreshUser,
    handleChangeReceiveBonuses,
    pathname,
  ]);

  const title = isUserCanIssueBonuses ? successTitle : warningTitle;
  const message = isUserCanIssueBonuses ? successMessage : warningMessage;

  const isEligible = isAuthenticated && !(!justEnabled && pendingUser);
  const isVisible = isEligible && (shouldBeVisible || justEnabled);

  useEffect(() => {
    onVisibilityChange?.(isVisible);
  }, [isVisible, onVisibilityChange]);

  if (!isEligible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          initial={false}
          key="bonus-notice"
          style={{ width: '100%' }}
          transition={{ duration: 0.4 }}
        >
          <ScReceiveBonusesNotice enabled={isUserCanIssueBonuses}>
            <div
              className={`icon ${isUserCanIssueBonuses ? 'success-icon' : 'warning-icon'}`}
            />
            <div className="text">
              <p className="title">
                {t(title || 'deposits:bonuses.toggleTitle', {
                  ns: translation,
                })}
              </p>
              <p className="message">
                <Trans
                  components={{
                    l: (
                      <span
                        onClick={() => navigate('/profile-custom/personal')}
                      />
                    ),
                  }}
                  i18nKey={message}
                  ns={translation}
                />
              </p>
            </div>
            <SwitchButton
              id="toggle-toggle-bonuses-notice"
              isChecked={isUserCanIssueBonuses}
              labelChecked={t('personal:on')}
              onChange={handleToggle}
            />
          </ScReceiveBonusesNotice>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
